import { gql } from '@apollo/client';
import currency from 'currency.js';
import {
  Roles_Enum,
  Shipping_Options,
  useGetShippingOptionsForCountryLazyQuery,
} from 'lib/graphql-generated';
import { useUser } from 'lib/hooks/use-user';
import { Dispatch, SetStateAction, useEffect } from 'react';
import { inputClassNames, labelClassNames } from './input';

const GET_SHIPPING_OPTIONS = gql`
  query GetShippingOptionsForCountry($country: String!, $shippableItems: Int!) {
    shipping_options(
      order_by: { price: asc }
      where: {
        _and: [
          {
            _or: [
              { country: { _eq: $country } }
              { regionByRegion: { countries: { code: { _eq: $country } } } }
            ]
          }
          { from: { _lte: $shippableItems } }
          {
            _or: [{ to: { _gte: $shippableItems } }, { to: { _is_null: true } }]
          }
        ]
      }
    ) {
      id
      name
      description
      region
      country
      price
    }
  }
`;

export interface ShippingSelectorValue {
  id: string;
  name: string;
  price: number;
}

export const SerializeShippingOption = (
  option: Partial<Shipping_Options>
): ShippingSelectorValue => {
  return { id: option.id, name: option.name, price: option.price };
};

interface Props {
  label?: string;
  id?: string;
  value: ShippingSelectorValue;
  setValue: Dispatch<SetStateAction<ShippingSelectorValue>>;
  className?: string;
  shippableProducts?: number;
  country: string;
}

export const ShippingSelector = ({
  value,
  setValue,
  country,
  label,
  className,
  id,
  shippableProducts,
}: Props) => {
  const { userData } = useUser();
  const [load, { data, loading, networkStatus }] =
    useGetShippingOptionsForCountryLazyQuery({
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    if (!!country)
      load({
        variables: {
          country: country,
          shippableItems: shippableProducts ?? 0,
        },
      });
  }, [country, shippableProducts]);

  const innerId = id ?? 'shippingDestinationSelector';

  return (
    <label htmlFor={innerId} className={`${className} ${labelClassNames}`}>
      {label}
      <select
        id={innerId}
        name={innerId}
        className={inputClassNames}
        value={value.id || ''}
        onChange={(e) => {
          if (e.target.value === 'custom') {
            setValue({ id: 'custom', name: 'Custom', price: 0 });
          } else {
            setValue(
              data.shipping_options
                .filter((o) => o.id === e.target.value)
                .map(
                  (o): ShippingSelectorValue => SerializeShippingOption(o)
                )[0]
            );
          }
        }}
        disabled={!country || loading}>
        <option value='' disabled className='text-gray-500'>
          {!country ? 'Please enter a country' : 'Select a shipping option'}
        </option>
        {userData?.role === Roles_Enum.Bookseller && (
          <option value='custom'>Custom</option>
        )}
        {data?.shipping_options.map((o) => (
          <option key={`shipping_option+${o.id}`} value={o.id}>
            {o.name} {o.region && ` - (${o.region})`}
            {` - ${currency(o.price, {
              fromCents: true,
              symbol: '£',
            }).format()}`}
          </option>
        ))}
      </select>
    </label>
  );
};
